import React, { useEffect, useState, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import CovertButton from "@cayuse/shared-react.components.covert-button";
import { DynamicTable } from "@cayuse/shared-react.components.dynamic-table";
import FormattedValue from "@cayuse/shared-react.components.formatted-value";
import { AlertDialog } from "@cayuse/shared-react.components.modal-dialog";
import { Icon } from "semantic-ui-react";
import { api } from "services";

import {
	AGREEMENTS_PRODUCT_KEY,
	ERROR_STATUS_CODE_400,
	ERROR_STATUS_CODE_403,
	ERROR_STATUS_CODE_404,
	ERROR_STATUS_CODE_500,
	FORM_SUBMISSION_TYPES,
	ROLE_AGREEMENTS_ADMINISTRATOR,
	ROLE_AGREEMENTS_USER,
	ROLE_INVENTIONS_ADMINISTRATOR,
	ROLE_INVENTIONS_LICENSE_OFFICE,
	ROLE_AGREEMENTS_RESEARCHER,
	STORAGE_AGREEMENTS_TABLE_SORTING
} from "globals/constants";
import { useDictionary, useFormAccesses, useHasRoles } from "hooks";
import { tenantSelectors } from "store/selectors";
import { notifyError, notifySuccess } from "utils";
import { getAgreementProduct } from "utils/agreements";
import { responseErrorHandler } from "utils/errorHandler";

import { agreementColumns } from "../constants";
import StartNewAgreement from "./StartNewAgreement";
import styles from "./styles.css";

const DEFAULT_DICTIONARY = {
	loadError: "Unable to retrieve linked agreement information, please refresh page or come back to this tab later.",
	deleteError: "Unable to remove link, please try again later.",
};

export const Agreements = ({ formType }) => {
	const { awardId, projectId } = useParams();
	const dictionary = useDictionary("agreements");
	const [productName, setProductName] = useState();
	const [agreements, setAgreements] = useState([]);
	const [sort, setSort] = useState({ direction: "descending", column: agreementColumns[0].sortKey });
	const [pageLimit, setPageLimit] = useState(10);
	const [pageOffset, setPageNumber] = useState(0);
	const [totalElements, setTotalElements] = useState();
	const [isLoading, setLoading] = useState(false);
	const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
	const [selectedAgreementId, setSelectedAgreementId] = useState();
	const tableRef = useRef(null);
	const tenantId = useSelector(tenantSelectors.tenantId);
	const formAccesses = useFormAccesses(FORM_SUBMISSION_TYPES.AWARD);

	const {
		[ROLE_INVENTIONS_ADMINISTRATOR]: isInventionAdmin,
		[ROLE_INVENTIONS_LICENSE_OFFICE]: isInventionLicense,
		[ROLE_AGREEMENTS_ADMINISTRATOR]: isAgreementsAdministrator,
		[ROLE_AGREEMENTS_USER]: isAgreementsUser,
		[ROLE_AGREEMENTS_RESEARCHER]: isAgreementsResearcher,
	} = useHasRoles([
		ROLE_INVENTIONS_ADMINISTRATOR,
		ROLE_INVENTIONS_LICENSE_OFFICE,
		ROLE_AGREEMENTS_ADMINISTRATOR,
		ROLE_AGREEMENTS_USER,
		ROLE_AGREEMENTS_RESEARCHER,
	]);

	const hasEditPermission = formAccesses.manage || formAccesses.updateForm;
	const hasAgreementRole = isInventionAdmin || isInventionLicense || isAgreementsUser || isAgreementsAdministrator || isAgreementsResearcher;

	const loadAgreements = useCallback(async (params = {}) => {
		if (!projectId && !awardId) {
			return null;
		}

		try {
			setLoading(true);
			const response = await api.agreements.getList({
				page: params.pageNumber ?? parseInt(pageOffset, 10),
				size: params.pageLimit ?? parseInt(pageLimit, 10),
				sort: `id%3A${params.direction === "ascending" ? "asc" : "desc"}`,
				awardId,
				projectId,
			});
			if (response?.content) {
				setAgreements(response.content);
				setTotalElements(response.totalElements);
			} else {
				setAgreements([]);
				setTotalElements(0);
			}
		} catch (error) {
			responseErrorHandler(error, {
				[ERROR_STATUS_CODE_400]: dictionary.errors.loadError || DEFAULT_DICTIONARY.loadError,
				[ERROR_STATUS_CODE_403]: dictionary.errors.loadError || DEFAULT_DICTIONARY.loadError,
				[ERROR_STATUS_CODE_404]: dictionary.errors.loadError || DEFAULT_DICTIONARY.loadError,
				[ERROR_STATUS_CODE_500]: dictionary.errors.loadError || DEFAULT_DICTIONARY.loadError,
			});

			setAgreements([]);
			setTotalElements(0);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		const load = async () => {
			const name = await getAgreementProduct(tenantId);
			if (name) {
				setProductName(name);

				if (hasAgreementRole) {
					let direction = sort.direction;
					try {
						const sorting = JSON.parse(localStorage.getItem(STORAGE_AGREEMENTS_TABLE_SORTING));
						if (sorting) {
							setSort(sorting);
							direction = sorting.direction;
						}
					} catch (storageError) {
						console.error("Error reading sort preferences from localStorage:", storageError);
					}
					loadAgreements({ direction });
				}
			}
		};

		load();
	}, []);

	const onPageChange = pageNumber => {
		setPageNumber(pageNumber - 1);
		loadAgreements({ pageNumber: pageNumber - 1, pageLimit, direction: sort.direction });
	};

	const onPageLimitChange = pageLimit => {
		setPageLimit(pageLimit);
		setPageNumber(0);
		loadAgreements({ pageOffset: 0, pageLimit, direction: sort.direction });
	};

	const handleSortChange = (column, direction) => {
		setSort({ direction, column: agreementColumns[0].sortKey });

		try {
			localStorage.setItem(
				STORAGE_AGREEMENTS_TABLE_SORTING,
				JSON.stringify({
					column,
					direction,
				}),
			);
		} catch (storageError) {
			console.error("Error saving sort preferences to localStorage:", storageError);
		}

		loadAgreements({ pageOffset, pageLimit, direction });
	};

	const handleConfirmDelete = async () => {
		try {
			if (!selectedAgreementId) {
				notifyError(dictionary.deleteError || "No agreement selected. Please try again.");

				return;
			}
			await api.agreements.remove(formType, awardId ?? projectId, selectedAgreementId);
			notifySuccess(dictionary.lindRemoved || "Link was removed successfully");
			loadAgreements({ direction: sort.direction });
		} catch (error) {
			responseErrorHandler(error, {
				[ERROR_STATUS_CODE_400]: dictionary.errors.deleteError || DEFAULT_DICTIONARY.deleteError,
				[ERROR_STATUS_CODE_403]: dictionary.errors.deleteError || DEFAULT_DICTIONARY.deleteError,
				[ERROR_STATUS_CODE_404]: dictionary.errors.deleteError || DEFAULT_DICTIONARY.deleteError,
				[ERROR_STATUS_CODE_500]: dictionary.errors.deleteError || DEFAULT_DICTIONARY.deleteError,
			});
		} finally {
			setIsConfirmDeleteOpen(false);
		}
	};

	const removeAgreement = id => {
		setSelectedAgreementId(id);
		setIsConfirmDeleteOpen(!isConfirmDeleteOpen);
	};

	const cellRenderer = ({ row, column, value }) => {
		if (column.keys.includes("agreementId")) {
			return (
				<a href={row.url}
					rel="noopener noreferrer"
					aria-label={`Open ${row.agreementTitle}`}
					title={`Open ${row.agreementTitle}`}
					target="_blank"
				>
					{value}
				</a>
			);
		} else if (column.keys.includes("executionDate")) {
			return <FormattedValue value={value} type="date" fallback="--" />;
		} else if (column.keys.includes("actions")) {
			return (
				hasEditPermission ? <CovertButton
					className={styles.iconButton}
					onClick={() => removeAgreement(row.agreementId)}
					aria-label={dictionary.removeAgreementButton || "Remove link"}
					data-testid="remove-agreement">
					<Icon
						className={styles.blue}
						name="trash"
						size="bigger"
					/>
				</CovertButton> : " "
			);
		}

		return value;
	};

	return (
		<div className={styles.container}>
			{isConfirmDeleteOpen &&
				<AlertDialog
					title={dictionary.removeAgreementTitle || "Remove link?"}
					description={formType === FORM_SUBMISSION_TYPES.PROJECT ?
						dictionary.removeAgreementDescription || "Removing this link from the project level will also remove it from any awards within this project." : ""}
					isOpen={isConfirmDeleteOpen}
					onConfirm={handleConfirmDelete}
					confirmText={dictionary.removeAgreementButton || "Remove link"}
					onCancel={() => setIsConfirmDeleteOpen(false)}
				/>
			}
			<div className={styles.headerSegment}>
				<span>{dictionary.title ?? "Agreements"}</span>
				{hasEditPermission && hasAgreementRole && productName &&
					<StartNewAgreement
						awardId={awardId}
						projectId={projectId}
						hasCreatePermission={isAgreementsAdministrator || isInventionAdmin || isInventionLicense}
						refresh={() => loadAgreements({ pageOffset, pageLimit, direction: sort?.direction })} />}
			</div>
			<div className={styles.content}>
				{!productName ? <div className={styles.message}>
					{dictionary.noAgreementProduct || "Agreements is not provisioned on this tenant."}
				</div> :
					!hasAgreementRole ? <div className={styles.message}>
						You need an Agreements role to have permission to see the contents of this section. Talk to an administrator if you believe you should have such a role
					</div> :
						<DynamicTable
							className={styles.agreementsTable}
							ref={tableRef}
							isLoading={isLoading}
							headers={agreementColumns}
							rows={agreements}
							totalItems={totalElements}
							pageSize={pageLimit}
							pageOffset={pageOffset}
							onPageChange={onPageChange}
							onPageSizeChange={onPageLimitChange}
							noResultsLabel={dictionary.noRecords || "No related records."}
							onSortChange={handleSortChange}
							sortColumn={sort.column}
							sortDirection={sort.direction}
							isSelectable={false}
							renderCell={cellRenderer}
						/>}
			</div>
		</div>
	);
};

export default Agreements;